import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { apiErr, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { courseImage } from "@/lib/images";
import { toast } from "sonner";
import { Download, FileText, Clock, Award, CheckCircle2, CreditCard, Loader2, GraduationCap, Target } from "lucide-react";

export default function CourseDetail() {
  const { slug } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const [c, setC] = useState(null);
  const [buying, setBuying] = useState(false);

  const load = () => api.get(`/courses/${slug}`).then((r) => setC(r.data)).catch(() => nav("/courses"));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [slug, user]);

  const buy = async () => {
    if (!user) { toast.info("Create an account to buy the course."); return nav("/register"); }
    setBuying(true);
    try {
      const { data } = await api.post("/payments/checkout", { course_slug: slug, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail));
      setBuying(false);
    }
  };

  if (!c) return <div className="min-h-[60vh] flex items-center justify-center text-slate-400">Loading…</div>;

  return (
    <div data-testid="course-detail-page">
      <section className="gradient-hero circuit-bg relative overflow-hidden border-b border-white/10">
        <img src={courseImage(c)} alt="" className="absolute right-0 top-0 h-full w-1/2 object-cover opacity-30 hidden lg:block" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0B132B] via-[#0B132B]/80 to-transparent" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-16 relative grid lg:grid-cols-3 gap-10">
          <div className="lg:col-span-2">
            <div className="flex gap-2 flex-wrap mb-4">
              <Badge className="bg-electric/20 text-electric border-electric/30">{c.provider}</Badge>
              <Badge variant="outline" className="border-gold/40 text-gold">{c.category}</Badge>
              <Badge variant="outline" className="border-white/20 text-slate-300">{c.level}</Badge>
            </div>
            <h1 className="font-display text-4xl lg:text-5xl font-extrabold text-white leading-tight">{c.credential || c.title}</h1>
            <p className="text-gold font-mono text-sm mt-2">{c.acronym}{c.scheme_code ? ` · ${c.scheme_code}` : ""} · {c.title}</p>
            <p className="text-lg text-slate-300 mt-3 max-w-2xl">{c.tagline}</p>
            <p className="text-sm font-mono text-gold mt-3">{c.cert_line}</p>
            <div className="flex gap-6 mt-6 text-sm text-slate-300">
              <span className="flex items-center gap-2"><Clock className="h-4 w-4 text-gold" />{c.hours} hours</span>
              <span className="flex items-center gap-2"><Award className="h-4 w-4 text-gold" />Certificate with unique code</span>
              <span className="flex items-center gap-2"><FileText className="h-4 w-4 text-gold" />Multiple-choice exam</span>
            </div>
          </div>

          <div className="glass rounded-2xl p-6 h-fit">
            <div className="flex items-end gap-2">
              <span className="font-display text-4xl font-extrabold text-white">{c.price}</span>
              <span className="text-slate-300 mb-1">{c.currency}</span>
            </div>
            <p className="text-xs text-slate-400 mt-1">Full bundle: course PDF + online exam + certification.</p>
            {c.recert_price != null && (
              <p className="text-xs text-slate-400 mt-1">Recertification every 3 years: <span className="text-gold font-semibold">{c.recert_price} {c.currency}</span></p>
            )}
            {c.owned ? (
              <Button className="w-full mt-5 btn-gold" data-testid="go-to-account-btn" onClick={() => nav("/account")}>
                <GraduationCap className="h-4 w-4 mr-2" /> You own this course — go to account
              </Button>
            ) : (
              <Button className="w-full mt-5 btn-gold h-12" data-testid="buy-course-btn" onClick={buy} disabled={buying}>
                {buying ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CreditCard className="h-4 w-4 mr-2" />}
                Buy the course
              </Button>
            )}
            <a href={`${API}/courses/${slug}/brochure`} target="_blank" rel="noreferrer" data-testid="download-brochure-btn" className="block">
              <Button variant="outline" className="w-full mt-3 border-white/20 text-white hover:bg-white/10 bg-transparent">
                <Download className="h-4 w-4 mr-2" /> Free brochure (PDF)
              </Button>
            </a>
            <ul className="mt-5 space-y-2 text-sm text-slate-300">
              {["Instant access after payment", "Schedule your online exam", "Publicly verifiable certificate"].map((t) => (
                <li key={t} className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-gold shrink-0" />{t}</li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-14 grid lg:grid-cols-3 gap-12">
        <div className="lg:col-span-2 space-y-10">
          <div>
            <h2 className="font-display text-2xl font-bold text-white mb-3">About the course</h2>
            <p className="text-slate-300 leading-relaxed">{c.summary}</p>
            <div className="mt-5 flex items-start gap-3 bg-surface rounded-lg p-4 border border-white/10">
              <Target className="h-5 w-5 text-gold shrink-0 mt-0.5" />
              <p className="text-sm text-slate-300"><span className="text-white font-semibold">Focus: </span>{c.focus}</p>
            </div>
          </div>

          <div>
            <h2 className="font-display text-2xl font-bold text-white mb-4">What you'll learn</h2>
            <div className="grid sm:grid-cols-2 gap-3">
              {c.outcomes.map((o, i) => (
                <div key={i} className="flex items-start gap-3 bg-surface-card rounded-lg p-4 border border-white/10" style={{ backgroundColor: "#131C38" }}>
                  <CheckCircle2 className="h-5 w-5 text-gold shrink-0 mt-0.5" />
                  <span className="text-sm text-slate-200">{o}</span>
                </div>
              ))}
            </div>
          </div>

          <div>
            <h2 className="font-display text-2xl font-bold text-white mb-2">Sample questions (free)</h2>
            <p className="text-sm text-slate-400 mb-4">A sample from the official exam set — correct answers are revealed only during the actual exam.</p>
            <Accordion type="single" collapsible className="space-y-2" data-testid="sample-questions">
              {c.sample_questions?.map((q) => (
                <AccordionItem key={q.id} value={`q${q.id}`} className="bg-surface-card border border-white/10 rounded-lg px-4" style={{ backgroundColor: "#131C38" }}>
                  <AccordionTrigger className="text-left text-slate-100 hover:no-underline">
                    <span className="whitespace-pre-wrap">{q.stem}</span>
                  </AccordionTrigger>
                  <AccordionContent>
                    <ul className="space-y-2">
                      {Object.entries(q.options).map(([k, v]) => (
                        <li key={k} className="text-sm text-slate-300"><span className="text-gold font-mono font-semibold mr-2">{k}.</span>{v}</li>
                      ))}
                    </ul>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>

        <aside className="space-y-5">
          <div className="bg-surface rounded-xl p-6 border border-white/10">
            <h3 className="font-display font-semibold text-white mb-3">Exam details</h3>
            <ul className="space-y-3 text-sm text-slate-300">
              <li className="flex justify-between"><span>Format</span><span className="text-white">Multiple-choice (A–D)</span></li>
              <li className="flex justify-between"><span>Questions</span><span className="text-white">40</span></li>
              <li className="flex justify-between"><span>Duration</span><span className="text-white">60 min</span></li>
              <li className="flex justify-between"><span>Pass mark</span><span className="text-gold font-semibold">75%</span></li>
              <li className="flex justify-between"><span>Certificate</span><span className="text-white">Automatic on pass</span></li>
            </ul>
          </div>
          <div className="bg-surface rounded-xl p-6 border border-white/10">
            <h3 className="font-display font-semibold text-white mb-2">Ideal for</h3>
            <p className="text-sm text-slate-300">{c.audience}</p>
          </div>
        </aside>
      </div>
    </div>
  );
}
